// DateAndTimeUtil.ts

import { TimeEntry, Day as EntryDay } from "../interfaces/EmployeeInterface";

// Define Day type for getGrandTotals function
interface Day {
    regularTime: number;
    overTime: number;
    doubleTime: number;
    totalWorkMinutes: number;
  }
  
  // Convert date string to mm/dd/yyyy format, adjusting for time zone offset
  export const getDate = (date: string): string => {
    const dateObj = new Date(date);
    dateObj.setHours(dateObj.getHours() + 8); // Adjust for time zone
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();
    return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
  };

  export const getDateString = (date: string | Date): string => {
    // If the input is a string, convert it to a Date object
    const dateObj = typeof date === "string" ? new Date(date) : date;
  
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      return "-"; // Return a default value for invalid dates
    }
  
    // Adjust for time zone offset (e.g., for UTC-8, you add 8 hours)
    dateObj.setHours(dateObj.getHours() + 8);
  
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();
    return `${month.toString().padStart(2, "0")}/${day.toString().padStart(2, "0")}/${year}`;
  };
  
  // Format decimal to hh:mm time format
  export const formatTime = (decimal: number): string => {
    const correctedDecimal = decimal / 60; // Convert minutes to hours
    const hours = Math.floor(correctedDecimal);
    const minutes = Math.round((correctedDecimal - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };
  
  // Calculate grand totals for regular time, overtime, and double time
  export const getGrandTotalTimes = (days: Day[]) => {
    let totalReg = 0;
    let totalOT = 0;
    let totalDT = 0;
  
    days.forEach((day) => {
      totalReg += day.regularTime || 0;
      totalOT += day.overTime || 0;
      totalDT += day.doubleTime || 0;
    });
  
    return {
      totalReg: formatTime(totalReg),
      totalOT: formatTime(totalOT),
      totalDT: formatTime(totalDT),
    };
  };

  // Calculate grand totals for regular time, overtime, and double time
  export const getGrandTotals = (days: Day[]) => {
    let totalReg = 0;
    let totalOT = 0;
    let totalDT = 0;
  
    days.forEach((day) => {
      totalReg += day.regularTime || 0;
      totalOT += day.overTime || 0;
      totalDT += day.doubleTime || 0;
    });
  
    return {
      totalReg: totalReg,
      totalOT: totalOT,
      totalDT: totalDT,
    };
  };

  // Calculate grand totals for regular time, overtime, and double time
  export const getTotalHours = (days: Day[]) => {
    let total = 0;
  
    days.forEach((day) => {
      total += day.totalWorkMinutes || 0;
    });
  
    return {
      totalMinutes: total
    };
  };
  
  // Apply timezone offset to UTC time and return formatted AM/PM time
  export const applyOffsetToUTC = (dateTime: string, offset: string): string => {
    if (!offset) {
      // If no offset is provided, return UTC time in AM/PM format
      const utcDate = new Date(dateTime);
      return formatAMPM(utcDate);
    }
    
    // Create a Date object from the input date
    const date = new Date(dateTime);
    
    // Calculate the offset in minutes (e.g., "-08:00" => -480 minutes)
    const [hoursOffset, minutesOffset] = offset.split(":").map(num => parseInt(num, 10));
    const totalOffsetMinutes = hoursOffset * 60 + minutesOffset;
    
    // Convert local time to UTC by adding the offset
    const utcDate = new Date(date.getTime() + totalOffsetMinutes * 60000);
    
    return formatAMPM(utcDate);
  };

  // Apply timezone offset to UTC time and return formatted AM/PM time
  export const applyOffsetToUTCDateTime = (dateTime: string | null | undefined, offset: string): string => {
    if (!dateTime) return "-";
  
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return "-"; // Invalid date string
  
    if (!offset) {
      const datePart = `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
      return `${datePart} ${formatAMPM(date)}`;
    }
  
    const [hoursStr, minutesStr] = offset.split(":");
    const hoursOffset = parseInt(hoursStr, 10);
    const minutesOffset = parseInt(minutesStr, 10);
    const totalOffsetMinutes = hoursOffset * 60 + Math.sign(hoursOffset) * minutesOffset;
  
    const adjustedDate = new Date(date.getTime() + totalOffsetMinutes * 60000);
  
    const datePart = `${adjustedDate.getUTCMonth() + 1}/${adjustedDate.getUTCDate()}/${adjustedDate.getUTCFullYear()}`;
    return `${datePart} ${formatAMPM(adjustedDate)}`;
  };  
  
  // Helper function to format a Date object as AM/PM time
  const formatAMPM = (date: Date): string => {
    let hours = date.getUTCHours(); // Use getUTCHours() to avoid system timezone effects
    let minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    hours = hours % 12; // Convert to 12-hour format
    hours = hours ? hours : 12; // Handle 0 hour case (midnight)
    let formattedMinutes = minutes.toString().padStart(2, '0'); // Ensure two-digit minute
    
    return `${hours}:${formattedMinutes} ${ampm}`;
  };
  
  // Function to calculate the time difference between "in" and "out" times in hh:mm format
  export const getTimeDiff = (inTime: string | Date, outTime: string | Date): string => {
    const inDate = new Date(inTime);
    const outDate = new Date(outTime);
    const timeDiffInMs = outDate.getTime() - inDate.getTime();
    const hours = Math.floor(timeDiffInMs / (1000 * 60 * 60)); // Calculate hours
    const minutes = Math.floor((timeDiffInMs % (1000 * 60 * 60)) / (1000 * 60)); // Calculate minutes
  
    return outTime === null ? "N/A" : `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };
  
  // Function to get the weekday name from a date string (e.g., "Monday")
  export const getWeekDay = (date: string): string => {
    const dateObj = new Date(date);
    dateObj.setHours(dateObj.getHours() + 8); // Adjust for time zone
    return dateObj.toLocaleDateString(undefined, { weekday: 'long' });
  };
  
  // Function to get the date in M/D format (excluding year, no leading zeros)
  export const getShortDate = (date: string): string => {
    const dateObj = new Date(date);
    const month = dateObj.getUTCMonth() + 1;
    const day = dateObj.getUTCDate();
    return `${month}/${day}`;
  };

  export const getFormattedOffset = (date: Date): string => {
    const offsetMinutes = date.getTimezoneOffset();
    const sign = offsetMinutes <= 0 ? "+" : "-";
  
    // Convert offset from minutes to hours and minutes
    const absOffsetMinutes = Math.abs(offsetMinutes);
    const hours = Math.floor(absOffsetMinutes / 60);
    const minutes = absOffsetMinutes % 60;
  
    // Format as +HH:MM or -HH:MM
    const formattedOffset = `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    return formattedOffset;
  }

  // Utility function to convert a UTC time and offset into local time
  export const convertToLocalTime = (dateTime: string, offset: string): Date => {
    const date = new Date(dateTime);
    const [hoursOffset, minutesOffset] = offset.split(":").map(num => parseInt(num, 10));
    const totalOffsetMinutes = hoursOffset * 60 + minutesOffset;
  
    // Apply the offset by adjusting the time
    const localTime = new Date(date.getTime() + totalOffsetMinutes * 60000);
    return localTime;
  };

// Function to handle the time entries and group them by date
export const adjustTimeEntriesForDay = (day: EntryDay): EntryDay => {
  const adjustedTimeEntries: TimeEntry[] = [];
  let regularTime = 0;
  let overTime = 0;
  let doubleTime = 0;
  let totalWorkMinutes = 0;

  day.timeEntries.forEach(entry => {
    // Convert the in and out times to local time using the respective offsets
    const localInTime = convertToLocalTime(entry.inDateTime, entry.inDateTimeOffset);
    const localOutTime = convertToLocalTime(entry.outDateTime, entry.outDateTimeOffset);
    
    // Check if the local out time crosses midnight
    const dayStart = new Date(localInTime);
    const dayEnd = new Date(localOutTime);
    
    // Compare the local out time to the original in date and adjust if necessary
    if (localOutTime < localInTime) {
      // If the out time is earlier than the in time (crosses midnight), adjust it back to the previous day
      localOutTime.setDate(localOutTime.getDate() - 1);
    }
    
    // Now that we have local times, calculate work minutes
    const workMinutes = Math.round((localOutTime.getTime() - localInTime.getTime()) / 60000); // Convert to minutes
    
    // For simplicity, categorize the time (regular, overtime, double time)
    if (workMinutes <= 480) {
      regularTime += workMinutes;
    } else if (workMinutes <= 720) {
      overTime += workMinutes;
    } else {
      doubleTime += workMinutes;
    }
    
    totalWorkMinutes += workMinutes;
    
    // Create a new time entry with adjusted times
    adjustedTimeEntries.push({
      ...entry,
      inDateTime: localInTime.toISOString(),
      outDateTime: localOutTime.toISOString(),
    });
  });

  // Return the updated Day with the adjusted time entries and new calculations
  return {
    ...day,
    regularTime,
    overTime,
    doubleTime,
    totalWorkMinutes,
    timeEntries: adjustedTimeEntries,
  };
};

// DateAndTimeUtil.ts
export const moveTimeToPreviousDay = (days: EntryDay[], entry: TimeEntry) => {
  const currentDay = days.find(day => day.date === entry.inDateTime.split('T')[0]);

  if (currentDay) {
    const currentDayIndex = days.indexOf(currentDay);

    if (currentDayIndex > 0) {
      const previousDay = days[currentDayIndex - 1];
      previousDay.timeEntries.push(entry);
    } else {
      const newDay: EntryDay = {
        date: getPreviousDay(currentDay.date),
        regularTime: 0,
        overTime: 0,
        doubleTime: 0,
        totalWorkMinutes: 0,
        totalClaimedAmount: 0,
        timeEntries: [entry],
        hourlyProjects: [],
        projectEmployeeTasks: [],
        employeeCustomerServiceWarrantyTasks: [],
        employeeSickClaims: [],
        employeeOperationalTasks: [],
        employeeWorkOrderTasks: [],
        fiveHourLunchException: false,
        fullLunchException: false,
        noLunchException: false,
      };
      days.unshift(newDay);
    }
  }
};

export const getPreviousDay = (currentDate: string): string => {
  const date = new Date(currentDate);
  date.setDate(date.getDate() - 1);
  return date.toISOString().split('T')[0];
};


  